import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import siteConfig from '../config/site-config.js';
import mapStateManager from '../utils/mapStateManager';
import {
  createDashboardRealtimeSocket,
  buildDashboardRealtimeHandlers,
  clearAllPickPointMarkers
} from '../utils/dashboard';
import {
  bootstrapDashboardMapInstance,
  waitForMapContainerReady,
  cleanupDashboardMapSessionOnUnmount
} from '../utils/dashboard/mapInitHelpers';
import {
  MAP_PROVIDER_CATALOG,
  buildRasterMapStyle
} from '../utils/mapProviderSettings';

/**
 * 密码通过后：等待容器 → 创建地图 → 建立 WS；卸载时清理
 */
export default function useDashboardMapWsBootstrap({
  enabled,
  mapContainer,
  mapRef,
  shouldUseEarthOverviewRef,
  isAnimationActiveRef,
  mapStyleReadyRef,
  drawingToolsActiveRef,
  membersRef,
  flyToLatestMemberRef,
  earthAnimationRef,
  startEarthAnimationRef,
  currentBasemapIdRef,
  mapProviderSettingsRef,
  activeOverlayIdsRef,
  setCurrentMapStyleId,
  setMapLoadingState,
  getStyleId,
  updateMarkers,
  renderTracks,
  loadRescueData,
  loadPickPoints,
  getMembers,
  settingsLoaded,
  trackModeApplied,
  fetchDevices,
  setMembers,
  displayPickPointOnMap,
  removePickPointFromMap,
  setPickPoints,
  displayRescueTargetOnMap,
  removeRescueTargetFromMap,
  setRescueTargets,
  displayRescueTrackOnMap,
  removeRescueTrackFromMap,
  setRescueTracks,
  stopEarthAnimation,
  clearRescueData,
  pickPointMarkersRef,
  onTrackerFieldMessage,
  onTrackerMessagePinRemoved,
  onTrackerIdentityUpdate,
  onTrackerCommandDispatch,
  onTrackerCommandPinRemoved,
  onCommandMissionBoardUpdated,
  loadFieldMessagePins,
  loadCommandPins,
  onPickPointSubmitted
}) {
  const updateMarkersRef = useRef(updateMarkers);
  const renderTracksRef = useRef(renderTracks);
  const loadRescueDataRef = useRef(loadRescueData);
  const loadPickPointsRef = useRef(loadPickPoints);
  const loadFieldMessagePinsRef = useRef(loadFieldMessagePins);
  const loadCommandPinsRef = useRef(loadCommandPins);
  const onTrackerCommandDispatchRef = useRef(onTrackerCommandDispatch);
  const onTrackerCommandPinRemovedRef = useRef(onTrackerCommandPinRemoved);
  const onCommandMissionBoardUpdatedRef = useRef(onCommandMissionBoardUpdated);
  const fetchDevicesRef = useRef(fetchDevices);
  const getMembersRef = useRef(getMembers);
  const getStyleIdRef = useRef(getStyleId);
  const onTrackerFieldMessageRef = useRef(onTrackerFieldMessage);
  const onTrackerMessagePinRemovedRef = useRef(onTrackerMessagePinRemoved);
  const onTrackerIdentityUpdateRef = useRef(onTrackerIdentityUpdate);
  const onPickPointSubmittedRef = useRef(onPickPointSubmitted);
  updateMarkersRef.current = updateMarkers;
  renderTracksRef.current = renderTracks;
  loadRescueDataRef.current = loadRescueData;
  loadPickPointsRef.current = loadPickPoints;
  loadFieldMessagePinsRef.current = loadFieldMessagePins;
  loadCommandPinsRef.current = loadCommandPins;
  onTrackerCommandDispatchRef.current = onTrackerCommandDispatch;
  onTrackerCommandPinRemovedRef.current = onTrackerCommandPinRemoved;
  onCommandMissionBoardUpdatedRef.current = onCommandMissionBoardUpdated;
  fetchDevicesRef.current = fetchDevices;
  getMembersRef.current = getMembers;
  getStyleIdRef.current = getStyleId;
  onTrackerFieldMessageRef.current = onTrackerFieldMessage;
  onTrackerMessagePinRemovedRef.current = onTrackerMessagePinRemoved;
  onTrackerIdentityUpdateRef.current = onTrackerIdentityUpdate;
  onPickPointSubmittedRef.current = onPickPointSubmitted;

  useEffect(() => {
    if (!enabled) return undefined;

    const mapContainerWaiterRef = { current: null };

    const actuallyInitializeMap = () => {
      const result = bootstrapDashboardMapInstance({
        mapboxgl,
        container: mapContainer.current,
        mapRef,
        shouldUseEarthOverviewRef,
        isAnimationActiveRef,
        mapStyleReadyRef,
        drawingToolsActiveRef,
        membersRef,
        flyToLatestMemberRef,
        earthAnimationRef,
        startEarthAnimationRef,
        stopEarthAnimation,
        currentBasemapIdRef,
        mapProviderSettingsRef,
        activeOverlayIdsRef,
        catalog: MAP_PROVIDER_CATALOG,
        buildRasterMapStyle,
        getSavedMapState: () => mapStateManager.getMapState('dashboard'),
        getDefaultMapState: () => mapStateManager.getDefaultMapState(),
        saveMapState: (state) => mapStateManager.saveMapState('dashboard', state),
        getStyleId: () => getStyleIdRef.current?.(),
        setCurrentMapStyleId,
        setMapLoadingState,
        updateMarkers: (...args) => updateMarkersRef.current?.(...args),
        renderTracks: (...args) => renderTracksRef.current?.(...args),
        loadRescueData: (...args) => loadRescueDataRef.current?.(...args),
        loadPickPoints: (...args) => {
          loadPickPointsRef.current?.(...args);
          loadFieldMessagePinsRef.current?.(...args);
          loadCommandPinsRef.current?.(...args);
        },
        getMembers: () => getMembersRef.current?.() || membersRef.current,
        settingsLoaded,
        trackModeApplied,
        fetchDevices: (...args) => fetchDevicesRef.current?.(...args)
      });

      if (!result.ok && result.reason !== 'already-ready' && result.reason !== 'no-container') {
        mapContainerWaiterRef.current?.start(500);
      }
    };

    const mapContainerWaiter = waitForMapContainerReady({
      getContainer: () => mapContainer.current,
      isMapReady: () => !!mapRef.current,
      onReady: actuallyInitializeMap
    });
    mapContainerWaiterRef.current = mapContainerWaiter;
    mapContainerWaiter.start(50);

    const { close: closeDashboardWs } = createDashboardRealtimeSocket({
      wsUrl: siteConfig.getWsUrl(),
      handlers: buildDashboardRealtimeHandlers({
        membersRef,
        setMembers,
        displayPickPointOnMap,
        removePickPointFromMap,
        setPickPoints,
        displayRescueTargetOnMap,
        removeRescueTargetFromMap,
        setRescueTargets,
        displayRescueTrackOnMap,
        removeRescueTrackFromMap,
        setRescueTracks,
        onTrackerFieldMessage: (payload) => onTrackerFieldMessageRef.current?.(payload),
        onTrackerMessagePinRemoved: (payload) => onTrackerMessagePinRemovedRef.current?.(payload),
        onTrackerIdentityUpdate: (payload) => onTrackerIdentityUpdateRef.current?.(payload),
        onTrackerCommandDispatch: (payload) => onTrackerCommandDispatchRef.current?.(payload),
        onTrackerCommandPinRemoved: (payload) => onTrackerCommandPinRemovedRef.current?.(payload),
      onCommandMissionBoardUpdated: (payload) => onCommandMissionBoardUpdatedRef.current?.(payload),
        onPickPointSubmitted: onPickPointSubmittedRef.current
          ? (point) => onPickPointSubmittedRef.current?.(point)
          : undefined
      })
    });

    return () => {
      mapContainerWaiter.stop();
      closeDashboardWs();
      cleanupDashboardMapSessionOnUnmount({
        mapStyleReadyRef,
        stopEarthAnimation,
        clearRescueData,
        clearPickPoints: () => clearAllPickPointMarkers(pickPointMarkersRef.current)
      });
    };
  }, [enabled]);
}
